import { ago, dayTime, sameDay, time } from "@shared/lib/format";

export interface ReminderChoice {
  label: string;
  at: Date;
}

/**
 * Сроки «Создать напоминание» — как в Discord: через полчаса, час и четыре
 * часа, ближайшие девять утра (сегодня или завтра) и понедельник в девять.
 * Совпавшие сроки (в воскресенье «завтра» и есть понедельник) не повторяются.
 */
export function reminderChoices(now: Date): ReminderChoice[] {
  const later = (minutes: number) => new Date(now.getTime() + minutes * 60_000);

  const nine = new Date(now);
  nine.setHours(9, 0, 0, 0);
  const today = nine > now;
  if (!today) nine.setDate(nine.getDate() + 1);

  // До ближайшего понедельника, не сегодняшнего: в понедельник — через неделю.
  const monday = new Date(now);
  monday.setHours(9, 0, 0, 0);
  monday.setDate(monday.getDate() + ((8 - monday.getDay()) % 7 || 7));

  const choices: ReminderChoice[] = [
    { label: "Через 30 минут", at: later(30) },
    { label: "Через 1 час", at: later(60) },
    { label: "Через 4 часа", at: later(240) },
    { label: today ? "Сегодня в 09:00" : "Завтра в 09:00", at: nine },
    { label: "В понедельник в 09:00", at: monday },
  ];
  return choices.filter(
    (choice, index) => choices.findIndex((other) => other.at.getTime() === choice.at.getTime()) === index,
  );
}

/** Время в окне «Другое…» — шагом в четверть часа, как в Discord. */
export const QUARTERS: string[] = Array.from({ length: 96 }, (_, index) => {
  const hours = Math.floor(index / 4);
  const minutes = (index % 4) * 15;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
});

/** Дата из поля `type="date"` и время «чч:мм» — в местном поясе. `null` — не разобрать. */
export function combine(date: string, clock: string): Date | null {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const hm = /^(\d{2}):(\d{2})$/.exec(clock);
  if (!day || !hm) return null;
  return new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]), Number(hm[1]), Number(hm[2]));
}

/** Ближайшая следующая четверть часа — с неё окно «Другое…» и открывается. */
export function nextQuarter(now: Date): Date {
  const at = new Date(now);
  at.setSeconds(0, 0);
  at.setMinutes(Math.floor(at.getMinutes() / 15) * 15 + 15);
  return at;
}

/** «2026-10-30» — для поля `type="date"` в местном поясе. */
export function dateField(at: Date): string {
  return `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, "0")}-${String(at.getDate()).padStart(2, "0")}`;
}

/** «14:15» — для списка времени. */
export function clockField(at: Date): string {
  return `${String(at.getHours()).padStart(2, "0")}:${String(at.getMinutes()).padStart(2, "0")}`;
}

/**
 * Подпись карточки в «Почте»: сработавшее — «Напоминание сработало 2 часа
 * назад», как в Discord; ждущее — когда напомнит.
 */
export function reminderNote(at: string | Date, fired: boolean, now: Date = new Date()): string {
  if (fired) return `Напоминание сработало ${ago(at)}`;
  return sameDay(at, now) ? `Напомню сегодня в ${time(at)}` : `Напомню ${dayTime(at)}`;
}
